import React, { useState, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Logo from "../../assets/logo_jothi.jpg";
import { LogoLoader } from "../../components/ui";
// amountToRelease / loanAmount / gst / insuranceAmount store PAISE.
import { toRupees } from "../../utils/money";

// Function to convert numbers to words
const convertToWords = (num) => {
  if (!num) return "";

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty',
    'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  if (num === 0) return 'Zero';

  // Handle negative numbers
  const negative = num < 0;
  num = Math.abs(num);

  let words = '';
  let scaleIndex = 0;

  while (num > 0) {
    // Handle each 2-digit group for Indian numbering system
    let groupNum;
    if (scaleIndex === 0) {
      // For ones, tens, hundreds
      groupNum = num % 1000;
      num = Math.floor(num / 1000);
    } else {
      // For thousands, lakhs, crores
      groupNum = num % 100;
      num = Math.floor(num / 100);
    }

    if (groupNum > 0) {
      // Convert the group to words
      let groupWords = '';

      if (scaleIndex === 0) {
        // Handle hundreds
        const hundreds = Math.floor(groupNum / 100);
        if (hundreds > 0) {
          groupWords += ones[hundreds] + ' Hundred';
          if (groupNum % 100 > 0) groupWords += ' ';
        }
        groupNum %= 100;
      }

      // Handle tens and ones
      if (groupNum > 0) {
        if (groupNum < 20) {
          groupWords += ones[groupNum];
        } else {
          groupWords += tens[Math.floor(groupNum / 10)];
          if (groupNum % 10 > 0) groupWords += ' ' + ones[groupNum % 10];
        }
      }

      // Add scale (thousand, lakh, crore)
      if (groupWords) {
        words = groupWords + (scales[scaleIndex] ? ' ' + scales[scaleIndex] : '') +
          (words ? ' ' + words : '');
      }
    }

    scaleIndex++;
  }

  return (negative ? 'Negative ' : '') + words;
};

// Simple component for promissory note view and download
const PromissoryNoteView = ({ personalDetailsData = {}, loanDetailsData = {}, memberDetails = {}, proposedLoanDetailsData = {}, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [filename, setFilename] = useState("promissory_note");
  const promissoryNoteRef = useRef(null);

  // Handle PDF download
  const handleDownload = async () => {
    if (!promissoryNoteRef.current) {
      setError("Cannot generate PDF at this time. Please try again.");
      return;
    }

    try {
      setIsLoading(true);

      // Use html2canvas to capture the content
      const canvas = await html2canvas(promissoryNoteRef.current, {
        scale: 2, // Higher resolution
        useCORS: true,
        logging: false,
      });

      // Create PDF with jsPDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      // Save the PDF
      pdf.save(`${filename}.pdf`);

    } catch (error) {
      console.error("PDF download error:", error);
      setError("Failed to download PDF. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle printing
  const handlePrint = () => {
    // Open the print dialog to customize filename
    setOpenPrintDialog(true);
  };

  // Function to execute the actual printing
  const executePrint = () => {
    setOpenPrintDialog(false);

    if (!promissoryNoteRef.current) {
      setError("Cannot print at this time. Please try again.");
      return;
    }

    try {
      setIsLoading(true);

      // Create a printable version by cloning the DOM element
      const printContent = promissoryNoteRef.current.cloneNode(true);

      // Create a new window for printing
      const printWindow = window.open('', '_blank');

      if (!printWindow) {
        setError("Pop-up blocked. Please allow pop-ups and try again.");
        setIsLoading(false);
        return;
      }

      // Setup print window content
      printWindow.document.open();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${filename}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
              }
              .print-container {
                max-width: 800px;
                margin: 0 auto;
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              ${printContent.outerHTML}
            </div>
            <script>
              // Auto print when loaded
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 500);
                }, 300);
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();

    } catch (error) {
      console.error("Print error:", error);
      setError("Failed to print. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Format data for display
  // Access data from multiple sources with proper fallbacks
  const borrowerName = personalDetailsData?.name || "";
  const address = personalDetailsData?.permanentStreetAddress || personalDetailsData?.permanentStreetAddress ||
    personalDetailsData?.address || "";

  // For loan amount, check multiple possible sources.
  // Use ?? not ||: amountToRelease is legitimately 0 until an admin approves the
  // loan. With ||, a real 0 fell through to the full loan amount and this legal
  // document overstated the disbursed figure.
  // PAISE -> RUPEES for the first two: amountToRelease and proposed loanAmount
  // were both converted by 20260909124252-money-to-paise-bigint-stage2. This is
  // a promissory note, so an unconverted figure would commit the borrower to
  // 100x the debt — and convertToWords below would spell it out in full.
  const loanAmount = memberDetails?.amountToRelease != null
    ? toRupees(memberDetails.amountToRelease)
    : loanDetailsData?.loanAmount != null
      ? toRupees(loanDetailsData.loanAmount)
      : loanDetailsData?.amount ?? "";

  const rateOfInterest = proposedLoanDetailsData?.rateOfInterest || "";

  // Convert amount to words
  const loanAmountWords = convertToWords(loanAmount);

  // Get date
  const currentDate = new Date();
  const formattedDate = `${currentDate.getDate().toString().padStart(2, '0')}.${(currentDate.getMonth() + 1).toString().padStart(2, '0')}.${currentDate.getFullYear()}`;

  const date = loanDetailsData?.date ||
    loanDetailsData?.loanDate ||
    formattedDate;

  // Get place
  const place = loanDetailsData?.place ||
    personalDetailsData?.city ||
    personalDetailsData?.currentCity ||
    "";

  // Generate default filename based on borrower's name if available
  React.useEffect(() => {
    if (borrowerName) {
      const sanitizedName = borrowerName.replace(/\s+/g, '_').toLowerCase();
      setFilename(`promissory_note_${sanitizedName}`);
    }
  }, [borrowerName]);

  console.log("Data being used for promissory note:", {
    borrowerName,
    address,
    loanAmount,
    loanAmountWords,
    date,
    place
  });

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h4" gutterBottom component="h1">
        Demand Promissory Note
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button variant="text" color="error" onClick={() => setError(null)} sx={{ ml: 2 }}>
            Close
          </Button>
        </Alert>
      )}

      {/* Promissory Note Content */}
      <Box
        ref={promissoryNoteRef}
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          p: 4,
          mb: 3,
          backgroundColor: "white",
          boxShadow: 1
        }}
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <img src={Logo} alt="Jothilinga Logo" style={{ width: "150px", height: "auto", marginBottom: "10px" }} />
          <Typography variant="h5" fontWeight="bold">
            DEMAND PROMISSORY NOTE
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography>
            <strong>Date:</strong> {date}
          </Typography>
          <Typography>
            <strong>Place:</strong> {place || "_______________"}
          </Typography>
        </Box>

        <Typography paragraph sx={{ textAlign: "justify" }}>
          On demand, I / We, <strong>{borrowerName || "_______________"}</strong>, residing at <strong>{address || "_______________"}</strong>, hereby promise to pay jointly and severally to <strong>JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</strong> or order, whenever demanded, the sum of <strong>Rs. {loanAmount ? `${loanAmount}/-` : "_______________"} (Rupees {loanAmountWords || "_______________"})</strong> at the rate of interest <strong>{rateOfInterest ? `${rateOfInterest}%` : "_______________"}</strong> per annum, for value received.
        </Typography>


        <Typography paragraph sx={{ mb: 3 }}>
          Presentment for payment and noting and protest of the note are hereby unconditionally waived.
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography><strong>Name/s of Borrower/s:</strong></Typography>
          <Typography>{borrowerName || "_______________"}</Typography>

          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ mr: 2 }}><strong>Signature/s:</strong></Typography>
              <Box sx={{ width: "200px", height: "2px", bgcolor: "black" }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Buttons for actions */}
      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          disabled={isLoading}
        >
          {isLoading ? <LogoLoader size="xs" className="p-0 inline-flex" /> : "Download PDF"}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={isLoading}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
        >
          Back
        </Button>
      </Box>

      {/* Filename Dialog for Print */}
      <Dialog open={openPrintDialog} onClose={() => setOpenPrintDialog(false)}>
        <DialogTitle>Print Document</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter a filename for your document. This name will appear in the browser's print dialog.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="filename"
            label="Filename"
            type="text"
            fullWidth
            variant="outlined"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPrintDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={executePrint} color="primary" variant="contained">
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromissoryNoteView;